/**
 * Why a CSV/XLSX stayed a file, and why an import failed. One map for the UI,
 * the upload responses and the agent's tool messages, so they cannot drift.
 */
import { getFileExtension } from '../utils/fileTypeDetection';
/**
 * The intake caps, one source for the backend's refusals and the browser's
 * pre-checks. Bytes are MiB, so "16 MB" in a message means 16 * 1,048,576.
 */
/** Largest workbook (XLSX, and the in-memory upload limit) the import will open. */
export const SPREADSHEET_MAX_BYTES = 25 * 1_048_576;
/** Largest CSV the import will parse: a CSV costs more to convert per byte than an XLSX. */
export const CSV_MAX_BYTES = 16 * 1_048_576;
/** Most cells a CSV may convert to (rows x columns), on import and on export. */
export const CSV_CELL_CAP = 200_000;
export const SPREADSHEET_INTAKE_NOTE = {
    MACRO_WORKBOOK: 'SPREADSHEET_INTAKE_MACRO_WORKBOOK',
    TOO_LARGE: 'SPREADSHEET_INTAKE_TOO_LARGE',
};
export const SPREADSHEET_INTAKE_NOTE_MESSAGES = {
    SPREADSHEET_INTAKE_MACRO_WORKBOOK: 'Kept as a file: macro-enabled workbooks cannot be edited in Bower. Save a copy as .xlsx to edit it.',
    SPREADSHEET_INTAKE_TOO_LARGE: 'Kept as a file: it is too large to edit as a spreadsheet (16 MB for CSV, 25 MB for Excel).',
};
/**
 * What a file is to spreadsheet intake, from its name and (when known) size
 * alone. Pure: each caller keeps its own gates (parent, intent, flag) and the
 * order it applies them in. An unknown size is never refused for size.
 */
export function classifySpreadsheetFile(fileName, sizeBytes) {
    const ext = getFileExtension(fileName);
    // Refused rather than stripped (spec D-11): a copy that silently loses its
    // macros is the "spreadsheet replica" researchers told us they do not want.
    if (ext === 'xlsm')
        return { kind: 'refused', note: SPREADSHEET_INTAKE_NOTE.MACRO_WORKBOOK };
    if (ext !== 'csv' && ext !== 'xlsx')
        return { kind: 'not-tabular' };
    const cap = ext === 'csv' ? CSV_MAX_BYTES : SPREADSHEET_MAX_BYTES;
    if (sizeBytes !== undefined && sizeBytes > cap) {
        return { kind: 'refused', note: SPREADSHEET_INTAKE_NOTE.TOO_LARGE };
    }
    return { kind: 'importable', format: ext };
}
/** Why Convert to spreadsheet, or Create > Import, refused a file before any import started. */
export const SPREADSHEET_CONVERT_MESSAGES = {
    WRONG_PARENT: 'A spreadsheet can only be created in a workspace or a collection.',
    NOT_CONVERTIBLE: 'Only .xlsx and .csv files can be converted to spreadsheets.',
    NO_STORED_FILE: 'This attachment has no stored file to convert.',
    NOT_IMPORTABLE: 'Only .xlsx and .csv files can be imported as spreadsheets.',
};
/** Terminal failure codes the import job writes to `processingData.task.errorCode`. */
export const SPREADSHEET_IMPORT_ERROR_MESSAGES = {
    SPREADSHEET_TOO_LARGE: 'This file is too large to edit as a spreadsheet. The original file is kept.',
    SPREADSHEET_TOO_MANY_CELLS: 'This file has more than 200,000 cells. The original file is kept.',
    VALIDATION_ERROR: 'This file could not be read as a spreadsheet. The original file is kept.',
    SPREADSHEET_IMPORT_FAILED: 'The spreadsheet engine could not open this file. The original file is kept.',
    SPREADSHEET_ENGINE_UNAVAILABLE: 'Spreadsheets were unavailable while this file was imported. Try again.',
    IMPORT_ABANDONED: 'The import did not finish in time. Try again.',
    ENQUEUE_FAILED: 'The import could not be started. Try again.',
    IMPORT_TRASHED: 'The import stopped because the spreadsheet was moved to the trash. Try again.',
    ACTOR_INACTIVE: 'The import was cancelled because the person who started it left this workspace.',
    SPREADSHEET_SAVE_FAILED: 'The spreadsheet could not be saved. Try again.',
};
const SPREADSHEET_IMPORT_FALLBACK_MESSAGE = 'The import failed. The original file is kept.';
/**
 * Server-side lifecycle of the spreadsheet import job. There is no STARTED:
 * the upload, chat or convert request that queued the import already has its
 * own event, and a second one for the same click would double the funnel.
 *
 * Bounded codes, formats and durations only: never a title, file name or cell.
 */
export const SPREADSHEET_IMPORT_EVENTS = {
    /** Properties: workspace_id, artifact_id, format, source, duration_ms. */
    COMPLETED: 'Spreadsheet Import Completed',
    /** Properties: workspace_id, artifact_id, error_code, source, duration_ms. */
    FAILED: 'Spreadsheet Import Failed',
};
export function spreadsheetImportErrorMessage(code) {
    return ((code &&
        Object.hasOwn(SPREADSHEET_IMPORT_ERROR_MESSAGES, code) &&
        SPREADSHEET_IMPORT_ERROR_MESSAGES[code]) ||
        SPREADSHEET_IMPORT_FALLBACK_MESSAGE);
}
//# sourceMappingURL=spreadsheet-intake.js.map